import { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import { 
  FaEnvelope, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaArrowLeft,
  FaShieldAlt,
  FaMobile,
  FaQrcode,
  FaGoogle,
  FaFacebookF,
  FaArrowRight
} from "react-icons/fa";
import logo from "../assets/logo.png";

// Color palette based on ABM Group colors
const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

const Login = () => {
    // Forgot Password Modal State
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [fpStep, setFpStep] = useState(1); // 1: verify, 2: set new password
    const [fpMobile, setFpMobile] = useState("");
    const [fpName, setFpName] = useState("");
    const [fpUniqueId, setFpUniqueId] = useState("");
    const [fpDob, setFpDob] = useState("");
    const [fpUserId, setFpUserId] = useState("");
    const [fpNewPassword, setFpNewPassword] = useState("");
    const [fpConfirmPassword, setFpConfirmPassword] = useState("");
    const [fpLoading, setFpLoading] = useState(false);
    const [fpError, setFpError] = useState("");
    const [fpSuccess, setFpSuccess] = useState("");
    // Forgot Password Handlers
    const handleForgotVerify = async (e) => {
      e.preventDefault();
      setFpError("");
      setFpSuccess("");
      if (!fpMobile || !fpName || !fpUniqueId || !fpDob) {
        setFpError("Please enter all required fields");
        return;
      }
      setFpLoading(true);
      try {
        const res = await axios.post("/auth/forgot-password/verify", {
          mobile: fpMobile,
          name: fpName,
          uniqueId: fpUniqueId,
          dateOfBirth: fpDob,
        });
        setFpUserId(res.data.userId);
        setFpStep(2);
      } catch (err) {
        setFpError(err.response?.data?.message || "User not found");
      } finally {
        setFpLoading(false);
      }
    };

    const handleForgotReset = async (e) => {
      e.preventDefault();
      setFpError("");
      setFpSuccess("");
      if (!fpNewPassword || !fpConfirmPassword) {
        setFpError("Please enter and confirm new password");
        return;
      }
      if (fpNewPassword !== fpConfirmPassword) {
        setFpError("Passwords do not match");
        return;
      }
      setFpLoading(true);
      try {
        await axios.post("/auth/forgot-password/reset", {
          userId: fpUserId,
          newPassword: fpNewPassword,
        });
        setFpSuccess("Password updated successfully! You can now login.");
        setTimeout(() => {
          setShowForgotModal(false);
          setFpStep(1);
          setFpMobile("");
          setFpName("");
          setFpUserId("");
          setFpNewPassword("");
          setFpConfirmPassword("");
          setFpError("");
          setFpSuccess("");
        }, 2000);
      } catch (err) {
        setFpError(err.response?.data?.message || "Failed to reset password");
      } finally {
        setFpLoading(false);
      }
    };
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!identifier || !password) {
      setError("Please enter identifier and password");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const res = await axios.post("/auth/login", { identifier, password });
      
      // Store in localStorage if remember me is checked
      if (rememberMe) {
        localStorage.setItem("rememberedIdentifier", identifier);
      } else {
        localStorage.removeItem("rememberedIdentifier");
      }
      
      // Show KYC warning if present
      if (res.data.kycWarning) {
        setError(res.data.kycWarning);
        setTimeout(() => setError(""), 5000); // Clear after 5 seconds
      }
      
      // Do not carry a previous account's dashboard choice into this login.
      localStorage.removeItem("dashboardMode");
      login(res.data);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid mobile/email/ID or password");
    } finally {
      setLoading(false);
    }
  };

  // Load remembered identifier on component mount
  useEffect(() => {
    const rememberedIdentifier = localStorage.getItem("rememberedIdentifier");
    if (rememberedIdentifier) {
      setIdentifier(rememberedIdentifier);
      setRememberMe(true);
    }
  }, []);

  return (
    <div className="min-h-screen mt-24 flex items-center justify-center relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${colors.darkBlue}, ${colors.navyBlue}, ${colors.tealBlue})`
      }}
    >
      {/* Decorative circles */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-10"
        style={{ backgroundColor: colors.skyBlue, transform: 'translate(-30%, -30%)' }}></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-10"
        style={{ backgroundColor: colors.brightBlue, transform: 'translate(30%, 30%)' }}></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full opacity-5"
        style={{ backgroundColor: colors.lightCyan, transform: 'translate(-50%, -50%)' }}></div>

      {/* Main Login Card */}
      <div className="relative z-10 w-full max-w-md px-4">
        <div className="bg-white/95 backdrop-blur-sm shadow-2xl rounded-2xl p-8 border border-white/30">
          
          {/* Header with gradient */}
          <div className="text-center mb-8">
            <img 
              src={logo} 
              alt="ABM Logo" 
              className="w-20 h-20 rounded-full object-contain mx-auto mb-4"
            />
            {/* ABM letter boxes commented out */}
            {/* <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: colors.pinkMagenta, color: "blue" }}>A</div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl"
                style={{ backgroundColor: colors.freshGreen, color: "blue" }}>B</div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: colors.goldenYellow, color: "blue" }}>M</div>
            </div> */}
            
            <h2 className="text-3xl font-bold" style={{ color: colors.nearBlack }}>
              Welcome Back
            </h2>
            <p className="text-sm mt-2" style={{ color: colors.oliveBrown }}>
              Login to your ABM Group One Dashboard
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm text-white"
              style={{ backgroundColor: colors.reddishBrown }}>
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            
            {/* Identifier Field */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                Mobile Number, Email or Unique ID
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <FaEnvelope style={{ color: colors.brightBlue }} />
                </div>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="Enter mobile number, email or unique ID"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border transition focus:outline-none focus:ring-2"
                  style={{ 
                    borderColor: error && !identifier ? colors.reddishBrown : '#e2e8f0',
                    focusRingColor: colors.deepOrange
                  }}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <FaLock style={{ color: colors.deepOrange }} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-10 py-3 rounded-lg border transition focus:outline-none focus:ring-2"
                  style={{ 
                    borderColor: error && !password ? colors.reddishBrown : '#e2e8f0',
                    focusRingColor: colors.deepOrange
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? 
                    <FaEyeSlash style={{ color: colors.oliveBrown }} /> : 
                    <FaEye style={{ color: colors.oliveBrown }} />
                  }
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300 focus:ring-0"
                  style={{ accentColor: colors.deepOrange }}
                />
                <span className="text-sm" style={{ color: colors.oliveBrown }}>
                  Remember me
                </span>
              </label>
              
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-sm font-medium hover:underline"
                style={{ color: colors.deepOrange }}
              >
                Forgot Password?
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-white transition transform hover:-translate-y-1 hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ 
                background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})`
              }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Logging in...
                </>
              ) : (
                <>
                  Login to Dashboard <FaArrowRight />
                </>
              )}
            </button>
          </form>

          {/* Register Link */}
          <p className="text-center text-sm mt-6" style={{ color: colors.oliveBrown }}>
            Don't have an account?{' '}
            <Link 
              to="/register" 
              className="font-semibold hover:underline"
              style={{ color: colors.freshGreen }}
            >
              Register Now
            </Link>
          </p>

        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 mt-20 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm relative">
            <button
              className="absolute top-3 right-3 text-2xl text-gray-400 hover:text-gray-700 transition"
              onClick={() => setShowForgotModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-xl font-bold mb-4 text-center" style={{ color: colors.deepOrange }}>
              Forgot Password
            </h3>
            {fpError && (
              <div className="mb-3 p-2 rounded text-white text-sm" style={{ backgroundColor: colors.reddishBrown }}>
                {fpError}
              </div>
            )}
            {fpSuccess && (
              <div className="mb-3 p-2 rounded text-white text-sm" style={{ backgroundColor: colors.freshGreen }}>
                {fpSuccess}
              </div>
            )}
            {fpStep === 1 && (
              <form onSubmit={handleForgotVerify} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                    Registered Mobile Number
                  </label>
                  <input
                    type="text"
                    value={fpMobile}
                    onChange={e => setFpMobile(e.target.value)}
                    placeholder="Enter your mobile number"
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e2e8f0', focusRingColor: colors.deepOrange }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                    Registered Name
                  </label>
                  <input
                    type="text"
                    value={fpName}
                    onChange={e => setFpName(e.target.value)}
                    placeholder="Enter your registered name"
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e2e8f0', focusRingColor: colors.deepOrange }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                    Your Unique ID
                  </label>
                  <input
                    type="text"
                    value={fpUniqueId}
                    onChange={e => setFpUniqueId(e.target.value)}
                    placeholder="Enter your unique ID (e.g., ABM123456)"
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e2e8f0', focusRingColor: colors.deepOrange }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={fpDob}
                    onChange={e => setFpDob(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e2e8f0', focusRingColor: colors.deepOrange }}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-2 rounded-lg font-semibold text-white transition hover:shadow-md"
                  style={{ background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})` }}
                  disabled={fpLoading}
                >
                  {fpLoading ? "Verifying..." : "Verify Identity"}
                </button>
              </form>
            )}
            {fpStep === 2 && (
              <form onSubmit={handleForgotReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={fpNewPassword}
                    onChange={e => setFpNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e2e8f0', focusRingColor: colors.deepOrange }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: colors.oliveBrown }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={fpConfirmPassword}
                    onChange={e => setFpConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e2e8f0', focusRingColor: colors.deepOrange }}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-2 rounded-lg font-semibold text-white transition hover:shadow-md"
                  style={{ background: `linear-gradient(135deg, ${colors.deepOrange}, ${colors.burntOrange})` }}
                  disabled={fpLoading}
                >
                  {fpLoading ? "Updating..." : "Set New Password"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;